import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listAdminInvoices } from '../../api/invoices';

const typeOptions = [
  { value: '', label: 'All types' },
  { value: 'GUEST_RECEIPT', label: 'Guest receipt' },
  { value: 'HOST_SETTLEMENT', label: 'Host settlement' },
  { value: 'HV_TO_HOST_COMMISSION', label: 'Commission invoice' },
];

const badgeStyles = {
  issued: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  void: 'bg-amber-50 text-amber-700 ring-amber-100',
  refunded: 'bg-rose-50 text-rose-700 ring-rose-100',
  default: 'bg-slate-100 text-slate-700 ring-slate-200',
};

const StatusBadge = ({ value }) => {
  const key = String(value || '').toLowerCase();
  const cls = badgeStyles[key] || badgeStyles.default;
  const label = String(value || 'unknown').replace(/_/g, ' ');
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${cls}`}>
      {label}
    </span>
  );
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const formatAmount = (currency, amount) => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return `${currency || ''} ${amount || ''}`.trim();
  return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
};

const InvoicesList = ({ token }) => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ type: '', bookingId: '', from: '', to: '' });
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await listAdminInvoices({ ...filters, page, limit: pagination.limit }, token);
      setItems(res.data || []);
      setPagination({
        page: res.pagination?.page || page,
        limit: res.pagination?.limit || pagination.limit,
        total: res.pagination?.total || 0,
      });
    } catch (err) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.type]);

  const applyFilters = () => {
    load(1);
  };

  const pageCount = useMemo(() => {
    return Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 20)));
  }, [pagination]);

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Finance</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-600">Track guest receipts, settlements, and commissions.</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-5">
        <select
          value={filters.type}
          onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        >
          {typeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Booking id"
          value={filters.bookingId}
          onChange={(e) => setFilters((f) => ({ ...f, bookingId: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.from}
          onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <input
          type="date"
          value={filters.to}
          onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
          className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none"
        />
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Apply filters
        </button>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Invoice</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Booking</th>
              <th className="px-4 py-3">Issued</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {error && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-rose-600">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !items.length && !error && (
              <tr>
                <td colSpan={7} className="px-4 py-3 text-slate-500">
                  No invoices found.
                </td>
              </tr>
            )}
            {items.map((invoice) => {
              const total = invoice.totals?.grandTotal ?? invoice.totals?.total ?? 0;
              return (
                <tr key={invoice.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="text-sm font-semibold text-slate-900">{invoice.invoiceNumber}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{invoice.invoiceType?.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-3 text-slate-700">{invoice.stayDetails?.bookingCode || invoice.bookingId}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDate(invoice.issuedAt)}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                    {formatAmount(invoice.currency, total)}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge value={invoice.status} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/invoices/${invoice.id}`)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pageCount}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= pageCount}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoicesList;
